import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, Lock, ScrollText, ArrowRight, Sparkles, ShieldCheck, CalendarCheck, FileDown } from "lucide-react";
import { api, API, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACCEPT_QUOTE_CALENDLY = "https://calendly.com/swiles-sideb/final-scoping-call";

function NdaGate({ quote, onAccepted }) {
  const [name, setName] = useState(quote.client_name || "");
  const [email, setEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = name.trim() && email.includes("@") && accepted && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/project-quotes/${quote.id}/accept-nda`, { name, email });
      onAccepted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record NDA acceptance");
      setSubmitting(false);
    }
  };

  return (
    <section className="border-b border-border" data-testid="quote-nda-gate">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-2xl border border-border bg-ice-soft p-8 lg:p-10 max-w-3xl">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-[#ac62ea]" />
            <div className="label-mono">Confidential — NDA gate</div>
          </div>
          <h2 className="font-display text-3xl mt-3 tracking-tight font-semibold">
            Before you view this quote, please accept the Mutual NDA.
          </h2>
          <p className="text-foreground/75 mt-4 leading-relaxed">
            This proposal contains confidential scope, pricing, and engagement detail prepared
            specifically for {quote.client_name}. To view it, please confirm that you accept the
            mutual non-disclosure agreement at{" "}
            <Link
              to="/terms#confidentiality"
              target="_blank"
              rel="noopener"
              className="text-[#ac62ea] hover:underline font-medium"
              data-testid="quote-nda-terms-link"
            >
              Section 8 of the Online Services Agreement
            </Link>
            . Your acceptance is recorded with a timestamp.
          </p>

          <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="quote-nda-form">
            <label className="grid gap-2 text-sm">
              <span className="font-medium">Your full name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="rounded-lg border border-border bg-white px-4 py-3 outline-none focus:border-[#ac62ea] focus:ring-2 focus:ring-[#ac62ea]/20"
                placeholder="Jane Doe"
                data-testid="quote-nda-name"
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium">Your work email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-lg border border-border bg-white px-4 py-3 outline-none focus:border-[#ac62ea] focus:ring-2 focus:ring-[#ac62ea]/20"
                placeholder="jane@acme.com"
                data-testid="quote-nda-email"
              />
            </label>
            <label className="flex items-start gap-3 text-sm leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#ac62ea]"
                data-testid="quote-nda-checkbox"
              />
              <span>
                I have read and accept the{" "}
                <Link
                  to="/terms#confidentiality"
                  target="_blank"
                  rel="noopener"
                  className="text-[#ac62ea] hover:underline font-medium"
                >
                  Mutual NDA (Section 8)
                </Link>{" "}
                on behalf of myself and my organization. I understand my acceptance is recorded
                with a timestamp.
              </span>
            </label>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                className="bg-[#ac62ea] hover:bg-[#9851d6] text-white"
                data-testid="quote-nda-accept-btn"
              >
                {submitting ? "Recording…" : "Accept NDA & view quote"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> Timestamp, IP, and user-agent are logged for legal record.
              </span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function ExpiredNotice({ quote }) {
  return (
    <section className="border-b border-border" data-testid="quote-expired">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="rounded-2xl border border-border bg-ice-soft p-8 lg:p-10 max-w-3xl">
          <div className="flex items-center gap-3">
            <Lock className="h-5 w-5 text-rose-600" />
            <div className="label-mono text-rose-700">Link expired</div>
          </div>
          <h2 className="font-display text-3xl mt-3 tracking-tight font-semibold">
            This quote link has expired.
          </h2>
          <p className="text-foreground/75 mt-4 leading-relaxed">
            The private share link for <strong>{quote.project_title}</strong>{quote.expires_at ? <> lapsed on {quote.expires_at}</> : null}. Please reach out to{" "}
            <a href="mailto:rev@sideb.io" className="text-[#ac62ea] hover:underline font-medium">
              rev@sideb.io
            </a>{" "}
            and we&rsquo;ll refresh it in under an hour.
          </p>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ s, role = "alternative", notes }) {
  const showPrice = !s.quote_only && (s.price || 0) > 0;
  const isRecommended = role === "recommended";
  const badgeStyle = {
    recommended: { bg: "bg-[#ac62ea]", text: "Recommended for this engagement", icon: <Sparkles className="h-3 w-3" /> },
    alternative: { bg: "bg-foreground/85", text: "Alternative", icon: null },
    add_on: { bg: "bg-emerald-600", text: "Add-on", icon: null },
  }[role] || null;
  return (
    <div
      className={`relative rounded-2xl border bg-white p-7 lg:p-8 transition-all ${
        isRecommended
          ? "border-[#ac62ea] shadow-[0_8px_30px_-8px_rgba(172,98,234,0.35)]"
          : "border-border"
      }`}
      data-testid={`quote-pricing-card-${s.sku}`}
    >
      {badgeStyle && (
        <div
          className={`absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full ${badgeStyle.bg} px-3 py-1 text-xs font-semibold text-white shadow-sm`}
          data-testid={`quote-${role}-badge-${s.sku}`}
        >
          {badgeStyle.icon} {badgeStyle.text}
        </div>
      )}
      <div className="label-mono">{s.category}</div>
      <h3 className="font-display text-2xl mt-2 font-semibold tracking-tight">{s.title}</h3>
      <div className="mt-4 flex items-baseline gap-2">
        {showPrice ? (
          <>
            <span className="font-display text-4xl font-semibold">{fmtUSD(s.price)}</span>
            {s.recurring && <span className="text-sm text-muted-foreground">/ month</span>}
          </>
        ) : (
          <span className="font-display text-2xl font-semibold text-foreground/70">
            Custom quote recommendation
          </span>
        )}
      </div>
      {s.deliverable && (
        <p className="text-foreground/75 mt-5 text-sm leading-relaxed">{s.deliverable}</p>
      )}
      {notes && (
        <div
          className="mt-5 rounded-lg border border-[#ac62ea]/25 bg-[#ac62ea]/[0.06] p-4"
          data-testid={`quote-card-notes-${s.sku}`}
        >
          <div className="label-mono mb-1 text-[#7837c2]">Why this fits</div>
          <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line">{notes}</p>
        </div>
      )}
      {s.best_for && (
        <div className="mt-5 pt-5 border-t border-border">
          <div className="label-mono mb-2">Best for</div>
          <p className="text-sm text-foreground/75 leading-relaxed">{s.best_for}</p>
        </div>
      )}
      <div className="mt-6 text-xs text-muted-foreground">
        <Link
          to={s.refund_policy_url ? s.refund_policy_url.replace(/^https?:\/\/[^/]+/, "") : "/terms#refunds"}
          className="inline-flex items-center gap-1.5 hover:text-[#ac62ea] hover:underline"
        >
          <ScrollText className="h-3 w-3" /> Terms &amp; refund policy
        </Link>
      </div>
    </div>
  );
}

function PricingGrid({ services, selections }) {
  const serviceBySku = useMemo(
    () => Object.fromEntries(services.map((s) => [s.sku, s])),
    [services],
  );
  const groups = useMemo(() => {
    const buckets = { recommended: [], alternative: [], add_on: [] };
    (selections || []).forEach((sel) => {
      const svc = serviceBySku[sel.sku];
      if (!svc) return;
      const role = ["recommended", "alternative", "add_on"].includes(sel.role) ? sel.role : "alternative";
      buckets[role].push({ ...sel, role, service: svc });
    });
    return buckets;
  }, [selections, serviceBySku]);

  const recommended = groups.recommended[0];
  if (!recommended) return null;

  return (
    <div data-testid="quote-pricing-grid">
      <ServiceCard s={recommended.service} role="recommended" notes={recommended.notes} />

      {(groups.alternative.length > 0 || groups.add_on.length > 0) && (
        <div className="mt-12 grid md:grid-cols-2 gap-8 lg:gap-12" data-testid="quote-alt-addon-grid">
          {groups.alternative.length > 0 && (
            <div data-testid="quote-alternatives-section">
              <div className="label-mono">Alternatives</div>
              <h3 className="font-display text-xl mt-2 font-semibold tracking-tight">
                Or pick a different shape
              </h3>
              <p className="text-sm text-foreground/70 mt-1">
                Same engagement, different shape. We&rsquo;ll confirm scope on the final scoping call.
              </p>
              <div className="mt-6 grid gap-5">
                {groups.alternative.map((sel, i) => (
                  <ServiceCard
                    key={`alt-${i}-${sel.sku}`}
                    s={sel.service}
                    role="alternative"
                    notes={sel.notes}
                  />
                ))}
              </div>
            </div>
          )}

          {groups.add_on.length > 0 && (
            <div data-testid="quote-addons-section">
              <div className="label-mono">Add-ons</div>
              <h3 className="font-display text-xl mt-2 font-semibold tracking-tight">
                Optional layers on top
              </h3>
              <p className="text-sm text-foreground/70 mt-1">
                Layer any of these on top of the recommended engagement at any time.
              </p>
              <div className="mt-6 grid gap-5">
                {groups.add_on.map((sel, i) => (
                  <ServiceCard
                    key={`addon-${i}-${sel.sku}`}
                    s={sel.service}
                    role="add_on"
                    notes={sel.notes}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function QuotePage() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: q }, { data: srv }] = await Promise.all([
          api.get(`/project-quotes/${id}`),
          api.get(`/services`),
        ]);
        if (cancelled) return;
        setQuote(q);
        setServices(srv);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Could not load this quote");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center text-muted-foreground" data-testid="quote-loading">
        Loading quote…
      </div>
    );
  }

  if (notFound || !quote) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="quote-not-found">
        <h1 className="font-display text-4xl font-semibold tracking-tight">Quote not found</h1>
        <p className="text-muted-foreground mt-3">
          This link may have expired or been entered incorrectly. If you were expecting a quote, please
          reach out to{" "}
          <a href="mailto:rev@sideb.io" className="text-[#ac62ea] hover:underline">
            rev@sideb.io
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="quote-page">
      <Helmet>
        <title>{`Project quote · ${quote.client_name} | SideB`}</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
      </Helmet>

      {/* Hero */}
      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16">
          {quote.preview_mode && (
            <div
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 px-3 py-1.5 text-xs uppercase tracking-widest text-amber-200"
              data-testid="quote-preview-banner"
            >
              <ScrollText className="h-3.5 w-3.5" />
              Admin preview · {quote.email_sent_at ? "already sent to client" : "not yet sent to client"}
            </div>
          )}
          <div className="label-mono" style={{ color: "rgba(255,255,255,0.55)" }}>Project quote · Confidential</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-5 tracking-[-0.04em] leading-[1.0] max-w-4xl">
            {quote.project_title}
          </h1>
          <p className="text-white/65 mt-5" data-testid="quote-hero-client">
            Prepared for <span className="text-white font-medium">{quote.client_name}</span>
          </p>
          {quote.company_name && (
            <p className="text-white/50 text-sm mt-1" data-testid="quote-hero-company">
              Company: <span className="text-white/75">{quote.company_name}</span>
            </p>
          )}
        </div>
      </section>

      {quote.expired && !quote.preview_mode ? (
        <ExpiredNotice quote={quote} />
      ) : !quote.nda_accepted ? (
        <NdaGate quote={quote} onAccepted={setQuote} />
      ) : (
        <>
          {/* Scope */}
          <section className="border-b border-border" data-testid="quote-scope-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
              <div className="label-mono">Project scope</div>
              <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">What we&rsquo;re solving</h2>
              <div className="prose prose-neutral max-w-3xl mt-6 text-foreground/85 leading-relaxed">
                {quote.scope.split("\n\n").map((para, i) => (
                  <p key={`scope-${i}-${para.slice(0, 24)}`} className="mb-4">{para}</p>
                ))}
              </div>
            </div>
          </section>

          {/* Deliverables */}
          <section className="border-b border-border bg-ice-soft" data-testid="quote-deliverables-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
              <div className="label-mono">Deliverables</div>
              <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">What you&rsquo;ll receive</h2>
              <ul className="mt-6 space-y-3 max-w-3xl" data-testid="quote-deliverables-list">
                {quote.deliverables.map((d, i) => (
                  <li key={`deliv-${i}-${d.slice(0, 24)}`} className="flex gap-3 text-foreground/85 leading-relaxed">
                    <Check className="h-5 w-5 text-[#ac62ea] flex-shrink-0 mt-0.5" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-6">
                Final deliverables may vary depending on scope, in-flight work status, and decisions
                taken during the engagement.
              </p>
            </div>
          </section>

          {/* Pricing — recommended + alternatives */}
          <section className="border-b border-border" data-testid="quote-pricing-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
              <div className="label-mono">Recommended engagement</div>
              <h2 className="font-display text-3xl mt-2 tracking-tight font-semibold">
                The shape we&rsquo;d run this as
              </h2>
              <p className="text-foreground/75 mt-3 max-w-2xl leading-relaxed">
                Based on the scope above, this is the SideB engagement that fits best. If a different shape fits, we&rsquo;ll adjust scope on the final scoping call.
              </p>
              <div className="mt-10">
                <PricingGrid services={services} selections={quote.selections} />
              </div>
            </div>
          </section>

          {/* Accept Quote CTA */}
          <section data-testid="quote-cta-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 text-center">
              <h2 className="font-display text-3xl tracking-tight font-semibold">Ready to move forward?</h2>
              <p className="text-foreground/75 mt-4 max-w-xl mx-auto leading-relaxed">
                Accept this quote by booking the final scoping &amp; payment call. We&rsquo;ll lock scope,
                confirm timeline, and capture payment so SideB can kick off the engagement same week.
              </p>
              <div className="mt-8 flex flex-col items-center gap-3">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <Button
                    size="lg"
                    onClick={async () => {
                      api.post(`/project-quotes/${quote.id}/cta-clicked`, {}).catch(() => {});
                      window.open(ACCEPT_QUOTE_CALENDLY, "_blank", "noopener,noreferrer");
                    }}
                    className="bg-[#ac62ea] hover:bg-[#9851d6] text-white px-7 py-6 text-base"
                    data-testid="quote-accept-cta"
                  >
                    <CalendarCheck className="mr-2 h-5 w-5" />
                    Accept quote &amp; schedule final scoping call
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => {
                      // Sends the NDA cookie automatically with the request.
                      window.open(`${API}/project-quotes/${quote.id}/download.pdf`, "_blank");
                    }}
                    className="px-7 py-6 text-base border-foreground/20 hover:bg-foreground/[0.04] hover:text-foreground hover:border-foreground/40"
                    data-testid="quote-download-pdf"
                  >
                    <FileDown className="mr-2 h-5 w-5" />
                    Download as PDF
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Or reach{" "}
                  <a href="mailto:rev@sideb.io" className="text-[#ac62ea] hover:underline">rev@sideb.io</a>
                  {" "}with questions before booking. The PDF is governed by the same Mutual NDA you just accepted.
                </p>
              </div>
              <p className="text-xs text-muted-foreground mt-10">
                This quote is governed by the{" "}
                <Link to="/terms" target="_blank" rel="noopener" className="text-[#ac62ea] hover:underline">
                  Online Services Agreement
                </Link>
                . Once accepted in writing, this quote attaches as a legal addendum under{" "}
                <Link to="/terms#custom" target="_blank" rel="noopener" className="text-[#ac62ea] hover:underline">
                  Section 13
                </Link>
                .
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
